/**
 * Header Name Control
 *
 * Header element that shows the authenticated user's name. Renders only when an
 * active MSAL account exists and is removed from the DOM when the user is
 * signed out. The TODO List has its own left-panel nav item, so the name is a
 * static label and does not open the TODO view.
 *
 * Requirements: 1.1, 1.2, 1.5
 */

import React from 'react';
import { useMsal } from '@azure/msal-react';
import './HeaderNameControl.css';

// Maximum number of characters shown for the user's name (Req 1.1).
export const MAX_NAME_LENGTH = 64;
// Single-character ellipsis indicator so the truncated label stays within the limit.
export const ELLIPSIS = '…';

/**
 * Truncate a display name to at most MAX_NAME_LENGTH characters. When the input
 * exceeds the limit, the returned value ends with the ellipsis indicator and is
 * still at most MAX_NAME_LENGTH characters long (Req 1.1).
 */
export function truncateName(name) {
  const value = typeof name === 'string' ? name : '';
  if (value.length <= MAX_NAME_LENGTH) {
    return value;
  }
  return value.slice(0, MAX_NAME_LENGTH - ELLIPSIS.length) + ELLIPSIS;
}

/**
 * Resolve the label to render for an account: use the display name when present
 * and non-blank, otherwise fall back to the account's principal name/username
 * (Req 1.2). The result is truncated per Req 1.1.
 */
export function resolveHeaderLabel(account) {
  if (!account) {
    return '';
  }
  const name = account.name;
  const hasName = typeof name === 'string' && name.trim().length > 0;
  const base = hasName ? name : (account.username || '');
  return truncateName(base);
}

const HeaderNameControl = ({ className = '' }) => {
  const { instance, accounts } = useMsal();
  // Prefer the explicitly active account, falling back to the first known account.
  const activeAccount = (instance && instance.getActiveAccount()) || (accounts && accounts[0]);

  // Removed from the DOM when no user is authenticated (Req 1.5).
  if (!activeAccount) {
    return null;
  }

  const label = resolveHeaderLabel(activeAccount);

  return (
    <div className={`header-name-control ${className}`.trim()}>
      <span
        className="header-name-control__display user-pill"
        title={label}
      >
        <span className="header-name-control__icon" aria-hidden="true">📝</span>
        <span className="header-name-control__label">{label}</span>
      </span>
    </div>
  );
};

export default HeaderNameControl;
